import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  Tabs,
  Tab,
  alpha,
} from '@mui/material';
import {
  Search,
  PictureAsPdf,
  FileDownload,
  ArrowForward,
} from '@mui/icons-material';
import {
  ALL_REPORTS,
  REPORT_CATEGORIES,
  type ReportCatalogItem,
  type ReportCategory,
  type BuiltInReportType,
} from '../../config/reportsCatalog';

interface ReportsHubProps {
  onSelectBuiltIn: (type: BuiltInReportType) => void;
  onSelectExternal: (path: string) => void;
}

const ExportBadge: React.FC<{ formats: ReportCatalogItem['exportFormats'] }> = ({ formats }) => (
  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
    {formats.includes('pdf') && (
      <Chip
        icon={<PictureAsPdf sx={{ fontSize: '14px !important' }} />}
        label="PDF"
        size="small"
        variant="outlined"
        sx={{ height: 22, fontSize: '0.7rem' }}
      />
    )}
    {(formats.includes('excel') || formats.includes('csv')) && (
      <Chip
        icon={<FileDownload sx={{ fontSize: '14px !important' }} />}
        label="Excel"
        size="small"
        variant="outlined"
        sx={{ height: 22, fontSize: '0.7rem' }}
      />
    )}
  </Stack>
);

const ReportsHub: React.FC<ReportsHubProps> = ({ onSelectBuiltIn, onSelectExternal }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<ReportCategory>('all');

  const filteredReports = useMemo(() => {
    const q = search.trim().toLowerCase();
    return ALL_REPORTS.filter((report) => {
      const matchesCategory = category === 'all' || report.category === category;
      if (!matchesCategory) return false;
      if (!q) return true;
      const title = t(report.titleKey).toLowerCase();
      const desc = t(report.descriptionKey).toLowerCase();
      const keywordMatch = report.keywords.some((k) => k.toLowerCase().includes(q));
      return title.includes(q) || desc.includes(q) || keywordMatch || report.id.includes(q);
    });
  }, [search, category, t]);

  const handleClick = (report: ReportCatalogItem) => {
    if (report.builtInType) {
      onSelectBuiltIn(report.builtInType);
    } else if (report.path) {
      onSelectExternal(report.path);
    }
  };

  const categoryCounts = useMemo(() => {
    const counts: Record<ReportCategory, number> = {
      all: ALL_REPORTS.length,
      operations: 0,
      hr: 0,
      finance: 0,
      admin: 0,
    };
    ALL_REPORTS.forEach((r) => {
      counts[r.category] += 1;
    });
    return counts;
  }, []);

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'primary.contrastText',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
          {t('reports.hubTitle')}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mb: 2.5 }}>
          {t('reports.hubSubtitle')}
        </Typography>
        <TextField
          fullWidth
          placeholder={t('reports.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{
            maxWidth: 520,
            '& .MuiOutlinedInput-root': {
              bgcolor: 'background.paper',
              borderRadius: 2,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Tabs
        value={category}
        onChange={(_, v) => setCategory(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 3,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 44 },
        }}
      >
        {REPORT_CATEGORIES.map((cat) => (
          <Tab
            key={cat.id}
            value={cat.id}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {t(cat.labelKey)}
                <Chip
                  label={categoryCounts[cat.id]}
                  size="small"
                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                />
              </Box>
            }
          />
        ))}
      </Tabs>

      {filteredReports.length === 0 ? (
        <Card sx={{ p: 6, textAlign: 'center' }}>
          <Search sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">
            {t('reports.noReportsFound')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('reports.tryDifferentSearch')}
          </Typography>
        </Card>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
            },
            gap: 2,
          }}
        >
          {filteredReports.map((report) => {
            const Icon = report.icon;
            return (
              <Card
                key={report.id}
                elevation={0}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2.5,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: report.color,
                    boxShadow: (theme) => `0 8px 24px ${alpha(report.color, 0.18)}`,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardActionArea onClick={() => handleClick(report)} sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 2.5, height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1.5 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha(report.color, 0.12),
                          color: report.color,
                          flexShrink: 0,
                        }}
                      >
                        <Icon />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                          {t(report.titleKey)}
                        </Typography>
                        <Chip
                          label={t(`reports.categories.${report.category}`)}
                          size="small"
                          sx={{
                            mt: 0.75,
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                          }}
                        />
                      </Box>
                      <ArrowForward sx={{ color: 'text.disabled', fontSize: 20, mt: 0.5 }} />
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1.5, minHeight: 40, lineHeight: 1.5 }}
                    >
                      {t(report.descriptionKey)}
                    </Typography>
                    <ExportBadge formats={report.exportFormats} />
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ReportsHub;
